/**
 * The backend can refuse a lead's status change because the access token of the
 * ad platform that lead came from (Meta, TikTok, Google Ads, Snapchat…) is
 * unusable (back/middlewares/adEventTokenGuard.js, only when that platform's
 * <PLATFORM>_TOKEN_VALIDATION_ENABLED=true on the server). The refusal carries a
 * machine-readable `reason`; this file recognises it and holds the copy.
 *
 * ★ PLATFORM-AGNOSTIC. The reason is `<PREFIX>_TOKEN_<KIND>` (META_TOKEN_EXPIRED,
 * TIKTOK_TOKEN_INVALID…). Any platform is recognised from that pattern alone, so a
 * new one needs NO change here to be understood. AD_PLATFORMS below only improves
 * the wording (a proper name, the Secrets group); an unlisted platform falls back
 * to its prefix and still works.
 *
 * ★ RECOGNITION ONLY — NEVER ENFORCEMENT. The server decides; this just shows the
 * refusal properly. With the server flags off nothing matches and every status
 * flow behaves exactly as before.
 *
 * ★ ONLY THE FIVE KNOWN KINDS MATCH. Other errors keep their normal toast.
 */

import { showAdTokenBlock } from '../redux/utilSlice';

export const AD_TOKEN_KINDS = Object.freeze({
	EXPIRED: 'EXPIRED',
	INVALID: 'INVALID',
	PERMISSION_MISSING: 'PERMISSION_MISSING',
	CONFIGURATION_MISSING: 'CONFIGURATION_MISSING',
	UNAVAILABLE: 'UNAVAILABLE',
});

// The backend's wire suffix for each kind (the outage one is spelled out).
const KIND_BY_SUFFIX = Object.freeze({
	EXPIRED: AD_TOKEN_KINDS.EXPIRED,
	INVALID: AD_TOKEN_KINDS.INVALID,
	PERMISSION_MISSING: AD_TOKEN_KINDS.PERMISSION_MISSING,
	CONFIGURATION_MISSING: AD_TOKEN_KINDS.CONFIGURATION_MISSING,
	VALIDATION_UNAVAILABLE: AD_TOKEN_KINDS.UNAVAILABLE,
});

const REASON_PATTERN = /^([A-Z][A-Z0-9]*)_TOKEN_(EXPIRED|INVALID|PERMISSION_MISSING|CONFIGURATION_MISSING|VALIDATION_UNAVAILABLE)$/;

/**
 * Names for the platforms we know. `label` is what people call it; `secrets` is
 * the group they edit in Admin Settings → Secrets. Adding a platform here is
 * optional polish — see the file header.
 */
export const AD_PLATFORMS = Object.freeze({
	META: { label: 'Meta (Facebook)', secrets: 'Facebook' },
	TIKTOK: { label: 'TikTok', secrets: 'TikTok' },
	GOOGLEADS: { label: 'Google Ads', secrets: 'Google Ads' },
	SNAPCHAT: { label: 'Snapchat', secrets: 'Snapchat' },
});

const titleCase = (s) => s.charAt(0) + s.slice(1).toLowerCase();

export const platformInfo = (platform) =>
	AD_PLATFORMS[platform] || { label: titleCase(platform), secrets: titleCase(platform) };

/** `{ title, detail, adminCanFix }` for one kind, worded for one platform. */
export const adTokenCopy = (kind, platform) => {
	const { label } = platformInfo(platform);
	const copy = {
		[AD_TOKEN_KINDS.EXPIRED]: {
			title: `${label} access token expired`,
			detail: `The ${label} access token used to report lead updates has expired.`,
			adminCanFix: true,
		},
		[AD_TOKEN_KINDS.INVALID]: {
			title: `${label} access token is not valid`,
			detail: `The ${label} access token was revoked, or belongs to a different app.`,
			adminCanFix: true,
		},
		[AD_TOKEN_KINDS.PERMISSION_MISSING]: {
			title: `${label} access token is missing a permission`,
			detail: `The ${label} access token does not have a permission it needs to report lead updates.`,
			adminCanFix: true,
		},
		[AD_TOKEN_KINDS.CONFIGURATION_MISSING]: {
			title: `${label} integration is not fully set up`,
			detail: `The ${label} integration is missing a setting, so its access token cannot be checked.`,
			adminCanFix: true,
		},
		[AD_TOKEN_KINDS.UNAVAILABLE]: {
			title: `${label} could not be reached`,
			detail: `The ${label} access token could not be checked right now. This is usually temporary.`,
			adminCanFix: false,
		},
	};
	return copy[kind] || copy[AD_TOKEN_KINDS.UNAVAILABLE];
};

/**
 * @param {*} err  an axios error (`err.response.data`) or an RTK Query error
 *                 (`err.data`) — the two shapes status changes can fail with
 * @returns {{reason:string, platform:string, kind:string, message:string}|null}
 */
export const getAdTokenBlock = (err) => {
	const body = err?.response?.data ?? err?.data;
	if (!body || typeof body !== 'object') return null;

	// `reason` is what the server sends; `code` / `error` are tolerated. In
	// development `error` is an object, which can never match.
	const reason = body.reason ?? body.code ?? body.error;
	if (typeof reason !== 'string') return null;

	const match = REASON_PATTERN.exec(reason);
	if (!match) return null;

	return {
		reason,
		platform: match[1],
		kind: KIND_BY_SUFFIX[match[2]],
		message: typeof body.message === 'string' ? body.message : '',
	};
};

export const isAdTokenBlock = (err) => getAdTokenBlock(err) !== null;

/**
 * If `err` is an ad-platform token refusal, open the one blocking modal and return
 * true. Callers then SKIP their usual error toast (two notices for one failure is
 * noise) and let their own `finally` reset loading state.
 */
export const reportAdTokenBlock = (err, dispatch) => {
	const block = getAdTokenBlock(err);
	if (!block) return false;
	dispatch(showAdTokenBlock(block));
	return true;
};
